import { Controller } from "@hotwired/stimulus"
import maplibregl from "maplibre-gl"
import MaplibreDraw from "maplibre-gl-draw"

export default class extends Controller {
  static targets = ["map", "countBadge", "countNumber"]
  static values = { polygon: String, countUrl: String, queryUrl: String }

  connect() {
    try {
      this.#setup()
    } catch (e) {
      console.error("order-map-edit controller error:", e)
    }
  }

  disconnect() {
    this.map?.remove()
  }

  clearPolygon() {
    this.draw.deleteAll()
    this.countBadgeTarget.classList.add("hidden")
    document.getElementById("polygon-input").value = ""
  }

  #setup() {
    const existing = this.polygonValue ? JSON.parse(this.polygonValue) : null
    const mapOptions = {
      container: this.mapTarget,
      style: "https://tiles.openfreemap.org/styles/liberty",
      zoom: 10,
      doubleClickZoom: false
    }

    if (existing) {
      const coords = existing.coordinates[0]
      const bounds = coords.reduce(
        (b, c) => b.extend(c),
        new maplibregl.LngLatBounds(coords[0], coords[0])
      )
      mapOptions.bounds = bounds
      mapOptions.fitBoundsOptions = { padding: 280, animate: false }
    }

    this.map = new maplibregl.Map(mapOptions)
    this.map.on("error", (e) => console.error("MapLibre error:", e.error))

    this.draw = new MaplibreDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      styles: [
        { id: "gl-draw-polygon-fill", type: "fill", filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]], paint: { "fill-color": "#5C3317", "fill-opacity": 0.15 } },
        { id: "gl-draw-polygon-stroke-active", type: "line", filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]], paint: { "line-color": "#5C3317", "line-width": 2 } },
        { id: "gl-draw-polygon-fill-static", type: "fill", filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]], paint: { "fill-color": "#5C3317", "fill-opacity": 0.15 } },
        { id: "gl-draw-polygon-stroke-static", type: "line", filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]], paint: { "line-color": "#5C3317", "line-width": 2 } },
        { id: "gl-draw-polygon-midpoint", type: "circle", filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]], paint: { "circle-radius": 4, "circle-color": "#5C3317" } },
        { id: "gl-draw-vertex", type: "circle", filter: ["all", ["==", "$type", "Point"], ["==", "meta", "vertex"]], paint: { "circle-radius": 5, "circle-color": "#fff", "circle-stroke-width": 2, "circle-stroke-color": "#5C3317" } }
      ]
    })
    this.map.addControl(this.draw, "top-right")
    this.map.addControl(new maplibregl.NavigationControl(), "bottom-right")

    this.map.on("load", () => {
      this.#initAddressLayer()
      this.#queryViewport()

      if (existing) {
        this.draw.add({ type: "Feature", geometry: existing, properties: {} })
        this.#fetchCount(existing)
        this.countBadgeTarget.classList.remove("hidden")
      }
    })

    this.map.on("moveend", () => this.#queryViewport())

    this.map.on("draw.create", (e) => {
      const geometry = e.features[0].geometry
      this.#onPolygonChange(geometry)
      setTimeout(() => this.draw.changeMode("direct_select", { featureId: e.features[0].id }), 0)
    })
    this.map.on("draw.update", (e) => this.#onPolygonChange(e.features[0].geometry))
    this.map.on("draw.delete", () => this.clearPolygon())
    this.map.on("draw.modechange", (e) => {
      if (e.mode !== "simple_select") return
      const features = this.draw.getAll().features
      if (features.length === 0) return
      setTimeout(() => this.draw.changeMode("direct_select", { featureId: features[0].id }), 0)
    })
  }

  #initAddressLayer() {
    this.map.addSource("addresses", { type: "geojson", data: { type: "FeatureCollection", features: [] } })
    this.map.addLayer({
      id: "address-dots",
      type: "circle",
      source: "addresses",
      paint: {
        "circle-radius": 2,
        "circle-color": "#3b82f6",
        "circle-opacity": 0.6
      }
    })
  }

  #queryViewport() {
    clearTimeout(this._queryTimeout)
    this._queryTimeout = setTimeout(() => {
      const bounds = this.map.getBounds()
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      this.#fetchForViewport({
        type: "Polygon",
        coordinates: [[
          [sw.lng, sw.lat],
          [ne.lng, sw.lat],
          [ne.lng, ne.lat],
          [sw.lng, ne.lat],
          [sw.lng, sw.lat]
        ]]
      })
    }, 300)
  }

  async #fetchForViewport(geometry) {
    const token = document.querySelector("meta[name='csrf-token']").content
    const res = await fetch(this.queryUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ polygon: JSON.stringify(geometry) })
    })
    const geojson = await res.json()
    this.map.getSource("addresses")?.setData(geojson)
  }

  #onPolygonChange(geometry) {
    document.getElementById("polygon-input").value = JSON.stringify(geometry)
    this.countBadgeTarget.classList.remove("hidden")
    this.#fetchCount(geometry)
  }

  async #fetchCount(geometry) {
    this.countNumberTarget.textContent = "…"
    const token = document.querySelector("meta[name='csrf-token']").content
    const res = await fetch(this.countUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ polygon: JSON.stringify(geometry) })
    })
    const { count } = await res.json()
    this.countNumberTarget.textContent = count.toLocaleString()
  }
}
